import { CHDbObject, DbHelper, DbObject } from "@app/shared/model/db-object.model";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { PostgRestObject } from "../databases/postgrest.database";

type AlulaDbObject<T> = Record<string, T>;

export type ClareHomeDbCollection<T> = Record<string, T>;
export type ClareDbCollection<T> = Record<number, T>;

/**
 * @description Gets the type from the object's constructor. Hopefully remove this when all slices of state are updated
 * to no longer instantiate objects and simply use type guards and interfaces
 * @param {DbObject} type the instantiated object
 * @returns {typeof DbObject} the type guard
 */
export const getStaticType = (type: DbObject): typeof DbObject => <typeof DbObject>type.constructor; // eslint-disable-line @typescript-eslint/consistent-type-assertions

/**
 * @description Wraps API results in an object with the key name. This may not be type safe. An example would be:
 * An Entity object is returned from a source observable and the piped result from here would be { entity: Entity }
 * @template T the type of object
 * @param {Observable<T>} source$ the source observable
 * @param {string} keyName the key name to use in the object
 * @returns {Observable<Record<string, T>>} a T object wrapped in an object
 */
export const wrapResult = <T>(source$: Observable<T>, keyName: string): Observable<Record<string, T>> =>
    source$.pipe(map(result => ({ [keyName]: result })));

/**
 * @description Wraps API results in an object with the key name. This specifically handles a result that returns a collection.
 * This may not be type safe. An example would be:
 * An Entity object is returned from a source observable and the piped result from here would be { entity: Entity }
 * @template T the type of object
 * @param {Observable<ClareDbCollection<T>>} source$ the source observable
 * @param {string} keyName the key name to use in the object
 * @returns {Observable<Record<string, ClareDbCollection<T>>>} a T object wrapped in an object
 */
export const wrapBulkResult = <T>(
    source$: Observable<ClareDbCollection<T>>,
    keyName: string
): Observable<Record<string, ClareDbCollection<T>>> => source$.pipe(map(result => ({ [keyName]: result })));

/**
 * @description Converts an array result to an object. Unfortunately, not type safe...thinks fromDbObject returns any.
 * @template T
 * @param {T} type the type of the array items
 * @returns {(data: any) => T} the type of object we're returning
 */
export const objectifyDbResult =
    <T extends DbObject>(type: T): ((data: any) => T) =>
    (data: any): T =>
        getStaticType(type).fromDbObject(data);

/**
 * @description Converts an array of alula items to a collection
 * @template T
 * @param {T[]} arr the array of items to objectify
 * @returns {AlulaDbObject<T>} the objectified array
 */
export const objectify = <T extends DbObject>(arr: T[]): AlulaDbObject<T> =>
    arr.reduce((items, data) => {
        items[data.id] = data;
        return items;
    }, {});

/**
 * @description Converts an array of database items to a collection. Not truly type safe.
 * @template T
 * @param {T} type the type to convert the array item to
 * @returns {(arr: any[]) => ClareDbCollection<T>} the db collection
 */
export const objectifyDbResultArray =
    <T extends DbObject>(type: T): ((arr: any[]) => ClareDbCollection<T>) =>
    (arr: any[] = []): ClareDbCollection<T> =>
        arr.reduce((items, data) => {
            items[data.id] = objectifyDbResult(type)(data);
            return items;
        }, {});

/**
 * @description Takes an object from the database and normalizes the keys for use in the code
 * @template T the type model used in the code
 * @template D the type model used in the database
 * @param {DbHelper<T, D>} helper the helper class to get the fromDbObject method from
 * @returns {(dataArr: D) => T} the rekeyed object
 */
export const rekeyDbResult =
    <T extends PostgRestObject | CHDbObject, D extends PostgRestObject | CHDbObject>(helper: DbHelper<T, D>): ((dataArr: D) => T) =>
    (data: D): T =>
        helper.fromDbObject(data);

/**
 * @description Takes an object from the database and normalizes the keys for use in the code
 * @template T the type model used in the code
 * @template D the type model used in the database
 * @param {DbHelper<T, D>} helper the helper class to get the fromDbObject method from
 * @returns {(dataArr: D[]) => ClareDbCollection<T>} the rekeyed object
 */
export const rekeyDbResultArray =
    <T extends CHDbObject | PostgRestObject, D extends CHDbObject | PostgRestObject>(
        helper: DbHelper<T, D>
    ): ((dataArr: D[]) => ClareDbCollection<T>) =>
    (dataArr: D[]): ClareDbCollection<T> =>
        (dataArr || []).reduce((items, data) => {
            const jsData = rekeyDbResult(helper)(data);
            items[jsData.id] = jsData;
            return items;
        }, {});
